import cx from 'classnames';
import { Editor } from 'components/Editor';
import { useGetTinyConfig } from 'hooks/useGetTinyConfig';
import Parser from 'html-react-parser';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EditorHelper from '../../../global/EditorHelper';
import HelperFunctions from '../../../global/HelperFunctions';
import CrossRefModal from './CrossRefModal';

export default function BasicEditor({
    showRef = true,
    tags = [],
    name,
    onChange,
    value,
    id,
    placeholder = '',
    forceMinHeight = false,
    toolbarSelector = undefined,
}) {
    const [editorLoaded, setEditorLoaded] = useState(false);
    const [showEditor, setShowEditor] = useState(false);

    const wrapperClassName = cx('area-text-block basic-editor-text-block', {
        'area-text-block-has-editor': showEditor && editorLoaded,
        'area-text-block-no-editor': !showEditor || !editorLoaded,
        'area-text-block-min-height': forceMinHeight,
    });

    return (
        <div
            className={wrapperClassName}
            onClick={() => {
                if (!showEditor) {
                    setShowEditor(true);
                }
            }}
        >
            <BasicEditorInner
                value={value}
                name={name}
                id={id}
                onChange={onChange}
                showRef={showRef}
                tags={tags}
                showEditor={showEditor}
                editorLoaded={editorLoaded}
                setEditorLoaded={setEditorLoaded}
                setShowEditor={setShowEditor}
                placeholder={placeholder}
                toolbarSelector={toolbarSelector}
            />
        </div>
    );
}

export function BasicEditorInner({
    showRef,
    tags = [],
    name,
    onChange,
    value,
    id,
    showEditor,
    editorLoaded,
    setEditorLoaded,
    setShowEditor,
    placeholder = '',
    toolbarSelector,
}) {
    const { t } = useTranslation();
    const [showCrossRefModal, setShowCrossRefModal] = useState(false);
    const editorRef = useRef(null);
    const tinyConfig = useGetTinyConfig(toolbarSelector);
    const [options, setOptions] = useState(false);

    useEffect(() => {
        if (showEditor && !options) {
            const tinyOptions = tinyMceOptions(tags, showRef, toggleCrossRefModal, t);

            setOptions({
                ...tinyOptions,
                ...tinyConfig,
            });
        }
    }, [showEditor]);

    useEffect(() => {
        if (editorLoaded && showEditor && editorRef.current) {
            // Move cursor to end of content
            editorRef.current.selection.select(editorRef.current.getBody(), true);
            editorRef.current.selection.collapse(false);
        }
    }, [showEditor, editorLoaded]);

    const contentClassName = cx('mce-content-body', {
        'd-none': showEditor && !!options && editorLoaded,
    });
    const editorClassName = cx({
        'd-none': !showEditor || !options || !editorLoaded,
    });

    return (
        <>
            <div className={contentClassName}>
                {Parser(
                    (_.isNil(value) || value === '') && placeholder !== ''
                        ? `<p class="text-muted">${placeholder}</p>`
                        : value,
                )}
            </div>
            <div className={editorClassName}>
                {!!options && (
                    <>
                        {showCrossRefModal && (
                            <CrossRefModal editor={editorRef.current} toggleCrossRefModal={toggleCrossRefModal} />
                        )}
                        <Editor
                            id={'jsTinymceEditor-' + id}
                            init={options}
                            value={value}
                            onEditorChange={handleEditorChange}
                            onInit={(event, editor) => {
                                setEditorLoaded(true);
                                editorRef.current = editor;
                            }}
                            onBlur={() => setShowEditor(false)}
                        />
                    </>
                )}
            </div>
        </>
    );

    function handleEditorChange(content) {
        onChange({
            target: {
                name: name,
                value: content,
            },
        });
    }

    function toggleCrossRefModal() {
        setShowCrossRefModal(!showCrossRefModal);
    }
}

export const tinyMceOptions = (tags = [], showRef = true, toggleCrossRefModal, t, crossRefs = []) => {
    // define block type specific config sets or configs that need dynamic content/functions
    return {
        setup: (editor) => {
            if (tags.length > 0) {
                const tagNames = tags.map((tag) => tag.name);

                editor.ui.registry.addMenuButton('tags', {
                    text: t('global:editor.btn.tags'),
                    fetch: function (callback) {
                        const items = EditorHelper.generateTagMenu(tagNames, editor);
                        callback(items);
                    },
                });
            }
            if (toggleCrossRefModal) {
                // Button that opens the cross-reference modal
                editor.ui.registry.addButton('ref', {
                    text: t('global:editor.btn.crossRef'),
                    onAction: () => {
                        toggleCrossRefModal();
                    },
                });
            } else if (crossRefs && crossRefs.length > 0) {
                // Dropdown with related cross-references
                editor.ui.registry.addMenuButton('ref', {
                    text: t('global:editor.btn.crossRef'),
                    fetch: function (callback) {
                        const items = crossRefs.map((ref) => ({
                            type: 'menuitem',
                            text: ref.title,
                            onAction: () => {
                                // Build cross-reference string without undefined document
                                let newRef = `[Ref id=${ref.id} type=${ref.type}`;
                                if (ref.document) {
                                    newRef += ` document=${ref.document}`;
                                }
                                newRef += `]`;
                                editor.insertContent(newRef);
                            },
                        }));
                        callback(items);
                    },
                });
            }
            editor.ui.registry.addButton('startNumber', {
                text: t('global:editor.btn.startListWith'),
                onAction: function () {
                    const parent = editor.dom.getParent(editor.selection.getNode(), 'ol');

                    parent !== null &&
                        editor.windowManager.open({
                            title: t('global:editor.title.startNumber'),
                            body: {
                                type: 'panel',
                                items: [
                                    {
                                        type: 'input',
                                        inputMode: 'text',
                                        name: 'startNumber',
                                        label: t('global:editor.title.startNumber'),
                                    },
                                ],
                            },
                            buttons: [
                                {
                                    type: 'submit',
                                    text: 'OK',
                                },
                            ],
                            onSubmit: function (dialog) {
                                const data = dialog.getData();
                                const number = HelperFunctions.filterNumbers(data.startNumber);
                                parent.setAttribute('start', number);

                                dialog.close();
                            },
                        });
                },
                onSetup: (buttonApi) => {
                    // Make button disabled when no "ol" element is selected
                    editor.on('NodeChange', function (e) {
                        const isOrderedList =
                            e.element.nodeName.toLowerCase() === 'li' &&
                            e.parents[1] &&
                            e.parents[1].nodeName.toLowerCase() === 'ol';
                        buttonApi.setEnabled(isOrderedList);
                    });
                },
            });
        },
    };
};
